import { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { Timestamp } from 'firebase/firestore';

// Lost & Found types (matching municipality app structure)
export type LostAndFoundType = 'lost' | 'found';

export type LostAndFoundStatus = 'pending' | 'published' | 'rejected' | 'archived';

export enum LostAndFoundCategory {
  ELECTRONICS = 'electronics',
  PETS = 'pets',
  WALLETS_AND_PURSES = 'wallets_and_purses',
  KEYS = 'keys',
  BAGS_AND_BACKPACKS = 'bags_and_backpacks',
  JEWELRY = 'jewelry',
  CLOTHING_AND_ACCESSORIES = 'clothing_and_accessories',
  DOCUMENTS_AND_IDS = 'documents_and_ids',
  MONEY_AND_CARDS = 'money_and_cards',
  BOOKS_AND_NOTEBOOKS = 'books_and_notebooks',
  SPORTS_EQUIPMENT = 'sports_equipment',
  CHILDRENS_ITEMS = 'childrens_items',
  GLASSES_AND_SUNGLASSES = 'glasses_and_sunglasses',
  BICYCLES_AND_SCOOTERS = 'bicycles_and_scooters',
  TOOLS = 'tools',
  MUSICAL_INSTRUMENTS = 'musical_instruments',
  MEDICAL_DEVICES = 'medical_devices',
  VEHICLES_AND_PARTS = 'vehicles_and_parts',
  OTHER = 'other',
}

export interface LostAndFoundSubmitter {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  role: 'citizen' | 'admin';
}

export interface LostAndFoundRecord {
  id: string;
  municipalityId: string;
  title: string;
  description: string;
  type: LostAndFoundType;
  category: LostAndFoundCategory;
  images: string[];
  location: string | null;
  dateLostFound: Date | null;
  submittedBy: LostAndFoundSubmitter;
  submittedAt: Date;
  status: LostAndFoundStatus;
  resolvedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  viewCount: number;
  responseCount: number;
  approvedResponseCount: number;
}

interface LostAndFoundStatistics {
  total: number;
  pending: number;
  published: number;
  resolved: number;
  lost: number;
  found: number;
  totalViews: number;
  averageViews: number;
}

interface LostAndFoundListResponse {
  items: LostAndFoundRecord[];
  total: number;
  statistics: LostAndFoundStatistics;
}

// Helper to convert Firestore Timestamp to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
};

// Convert Firestore data to LostAndFoundRecord
const convertFirestoreData = (data: unknown, id: string, municipalityId: string): LostAndFoundRecord => {
  const docData = data as Record<string, unknown>;
  
  // Convert submittedBy
  const submittedByData = docData.submittedBy as Record<string, unknown>;
  const submittedBy: LostAndFoundSubmitter = {
    userId: (submittedByData.userId as string) || '',
    firstName: (submittedByData.firstName as string) || '',
    lastName: (submittedByData.lastName as string) || '',
    email: (submittedByData.email as string) || '',
    phone: (submittedByData.phone as string) || null,
    role: (submittedByData.role as 'citizen' | 'admin') || 'citizen',
  };

  return {
    id,
    municipalityId,
    title: (docData.title as string) || '',
    description: (docData.description as string) || '',
    type: (docData.type as LostAndFoundType) || 'lost',
    category: (docData.category as LostAndFoundCategory) || LostAndFoundCategory.OTHER,
    images: (docData.images as string[]) || [],
    location: (docData.location as string) || null,
    dateLostFound: docData.dateLostFound ? toDate(docData.dateLostFound) : null,
    submittedBy,
    submittedAt: toDate(docData.submittedAt || docData.createdAt),
    status: (docData.status as LostAndFoundStatus) || 'pending',
    resolvedAt: docData.resolvedAt ? toDate(docData.resolvedAt) : null,
    createdAt: toDate(docData.createdAt),
    updatedAt: toDate(docData.updatedAt),
    viewCount: (docData.viewCount as number) || 0,
    responseCount: (docData.responseCount as number) || 0,
    approvedResponseCount: (docData.approvedResponseCount as number) || 0,
  };
};

export const useMunicipalityLostAndFound = (municipalityId: string | null) => {
  const [data, setData] = useState<LostAndFoundListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setLoading(false);
      setData(null);
      return;
    }

    const fetchLostAndFound = async () => {
      try {
        setLoading(true);
        setError(null);

        const lostAndFoundCollection = collection(db, 'municipalities', municipalityId, 'lostAndFound');
        const snapshot = await getDocs(query(lostAndFoundCollection, orderBy('createdAt', 'desc')));

        const items: LostAndFoundRecord[] = [];
        let totalViews = 0;
        let pendingCount = 0;
        let publishedCount = 0;
        let resolvedCount = 0;
        let lostCount = 0;
        let foundCount = 0;

        snapshot.forEach((doc) => {
          const record = convertFirestoreData(doc.data(), doc.id, municipalityId);
          items.push(record);

          // Calculate statistics
          totalViews += record.viewCount || 0;
          
          // Count by status
          if (record.status === 'pending') {
            pendingCount++;
          } else if (record.status === 'published') {
            publishedCount++;
          }
          
          // Count resolved
          if (record.resolvedAt) {
            resolvedCount++;
          }
          
          // Count by type
          if (record.type === 'lost') {
            lostCount++;
          } else {
            foundCount++;
          }
        });

        const total = items.length;
        const averageViews = total > 0 ? Math.round(totalViews / total) : 0;

        setData({
          items,
          total,
          statistics: {
            total,
            pending: pendingCount,
            published: publishedCount,
            resolved: resolvedCount,
            lost: lostCount,
            found: foundCount,
            totalViews,
            averageViews,
          },
        });
      } catch (err) {
        console.error('Error fetching municipality lost and found:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch municipality lost and found');
      } finally {
        setLoading(false);
      }
    };

    fetchLostAndFound();
  }, [municipalityId]);

  return { data, loading, error };
};
